import React from 'react';

/**
 * AppErrorBoundary
 * ----------------
 * Top-level React error boundary. Wraps the entire app so any uncaught
 * render-time exception in a route or component surfaces as a friendly
 * fallback screen instead of a white page.
 *
 * Errors are also logged to `window.console.error` and (best-effort) sent
 * to `/api/telemetry/frontend-crash` so we can see them in server logs.
 */
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: (error && error.message) || 'Unknown error' };
  }

  componentDidCatch(error, info) {
    console.error('[AppErrorBoundary] uncaught render error:', error, info);
    try {
      const url = (process.env.REACT_APP_BACKEND_URL || '') + '/api/telemetry/frontend-crash';
      // Fire-and-forget; never awaited, never blocks fallback render.
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: (error && error.message) || 'unknown',
          stack: (error && error.stack) || '',
          component_stack: (info && info.componentStack) || '',
          path: typeof window !== 'undefined' ? window.location.pathname : '',
          ua: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        }),
        keepalive: true,
      }).catch(() => {});
    } catch (_e) { /* best-effort only */ }
  }

  handleReload = () => {
    try { window.location.reload(); } catch (_e) { /* noop */ }
  };

  handleHome = () => {
    try { window.location.assign('/'); } catch (_e) { /* noop */ }
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }
    return (
      <div
        data-testid="app-error-boundary-fallback"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          background: '#0f172a',
          color: '#f8fafc',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        <div style={{ maxWidth: 480, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
          <h1 style={{ fontSize: 24, margin: '0 0 8px', fontWeight: 600 }}>
            Something went wrong on this page
          </h1>
          <p style={{ opacity: 0.8, lineHeight: 1.5, margin: '0 0 20px' }}>
            The rest of HiBFF is still fine — this is just one screen. Try
            reloading, or head home. If it keeps happening, let us know at
            hello@hibff.com.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="app-error-reload-btn"
              style={{
                background: '#f99b7d',
                color: '#0f172a',
                border: 'none',
                borderRadius: 999,
                padding: '10px 20px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Reload
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              data-testid="app-error-home-btn"
              style={{
                background: 'transparent',
                color: '#f8fafc',
                border: '1px solid rgba(248,250,252,0.35)',
                borderRadius: 999,
                padding: '10px 20px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Go home
            </button>
          </div>
          {process.env.NODE_ENV !== 'production' && this.state.message ? (
            <pre
              style={{
                marginTop: 20,
                fontSize: 12,
                opacity: 0.6,
                whiteSpace: 'pre-wrap',
                textAlign: 'left',
              }}
            >
              {this.state.message}
            </pre>
          ) : null}
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
